import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Tabs, Tab, TextField, Button, Typography, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, CircularProgress
} from '@mui/material';
import { Plus, Save, Trash2, Pencil, Upload, Image as ImageIcon, ChevronDown } from 'lucide-react';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { PageHeader } from '../components/ui';
import './CareersManagement.css';
import './ModuleAdmin.css';

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const CareersManagement = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [tab, setTab] = useState(0);
  const [heroForm, setHeroForm] = useState({ title: '', subtitle: '', imageUrl: '' });
  const [whyForm, setWhyForm] = useState({ title: '', description: '', bullets: [], imageUrl: '' });
  const [lifeForm, setLifeForm] = useState({ title: '', subtitle: '', images: [] });
  const [positionsHeadings, setPositionsHeadings] = useState({ title: '', subtitle: '' });
  const [bulletInput, setBulletInput] = useState('');
  const heroImageInputRef = useRef(null);
  const whyImageInputRef = useRef(null);
  const lifeImageInputRef = useRef(null);
  const lifeReplacingIndexRef = useRef(null);

  const { data: careersData, isLoading } = useQuery('careers-admin', adminAPI.getCareers);
  const payload = careersData?.data?.data || {};

  useEffect(() => {
    if (!payload.hero) return;
    if (payload.hero) setHeroForm({ title: payload.hero.title || '', subtitle: payload.hero.subtitle || '', imageUrl: payload.hero.imageUrl || '' });
    if (payload.whyJoin) setWhyForm({ title: payload.whyJoin.title || '', description: payload.whyJoin.description || '', bullets: payload.whyJoin.bullets || [], imageUrl: payload.whyJoin.imageUrl || '' });
    if (payload.lifeAt) setLifeForm({ title: payload.lifeAt.title || '', subtitle: payload.lifeAt.subtitle || '', images: payload.lifeAt.images || [] });
    if (payload.positions) setPositionsHeadings({ title: payload.positions.title || '', subtitle: payload.positions.subtitle || '' });
  }, [payload.hero, payload.whyJoin, payload.lifeAt, payload.positions]);

  const categories = payload.positions?.categories || [];

  const updateHeroMutation = useMutation((data) => adminAPI.updateCareersHero(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Careers hero updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update hero.', severity: 'error' })
  });
  const updateWhyMutation = useMutation((data) => adminAPI.updateCareersWhy(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Why Join section updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update Why Join.', severity: 'error' })
  });
  const updateLifeMutation = useMutation((data) => adminAPI.updateCareersLife(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Life at Invincible updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update Life at Invincible.', severity: 'error' })
  });
  const updateHeadingsMutation = useMutation((data) => adminAPI.updateCareersPositionsHeadings(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Open Positions headings updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update headings.', severity: 'error' })
  });
  const createCategoryMutation = useMutation((data) => adminAPI.createJobCategory(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job category created.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to create category.', severity: 'error' })
  });
  const updateCategoryMutation = useMutation(({ id, data }) => adminAPI.updateJobCategory(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job category updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update category.', severity: 'error' })
  });
  const deleteCategoryMutation = useMutation((id) => adminAPI.deleteJobCategory(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job category deleted.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to delete category.', severity: 'error' })
  });
  const createJobMutation = useMutation(({ categoryId, data }) => adminAPI.createJob(categoryId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job created.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to create job.', severity: 'error' })
  });
  const updateJobMutation = useMutation(({ id, data }) => adminAPI.updateJob(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job updated.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to update job.', severity: 'error' })
  });
  const deleteJobMutation = useMutation((id) => adminAPI.deleteJob(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('careers-admin');
      showToast({ message: 'Job deleted.', severity: 'success' });
    },
    onError: (e) =>
      showToast({ message: e.response?.data?.message || 'Failed to delete job.', severity: 'error' })
  });

  const addBullet = () => {
    if (!bulletInput.trim()) return;
    setWhyForm((p) => ({ ...p, bullets: [...(p.bullets || []), bulletInput.trim()] }));
    setBulletInput('');
  };

  const removeBullet = (i) => {
    setWhyForm((p) => ({ ...p, bullets: (p.bullets || []).filter((_, idx) => idx !== i) }));
  };

  const addLifeImage = (url) => {
    if (!url?.trim()) return;
    setLifeForm((p) => ({ ...p, images: [...(p.images || []), url.trim()] }));
  };

  const removeLifeImage = (i) => {
    setLifeForm((p) => ({ ...p, images: (p.images || []).filter((_, idx) => idx !== i) }));
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url;
      if (url) {
        setHeroForm((p) => ({ ...p, imageUrl: url }));
        updateHeroMutation.mutate({ ...heroForm, imageUrl: url });
      }
    } catch (err) {
      showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
    }
    e.target.value = '';
  };

  const handleWhyImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url;
      if (url) {
        setWhyForm((p) => ({ ...p, imageUrl: url }));
        updateWhyMutation.mutate({ ...whyForm, imageUrl: url });
      }
    } catch (err) {
      showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
    }
    e.target.value = '';
  };

  const handleLifeImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    e.target.value = '';
    const replacing = lifeReplacingIndexRef.current;
    lifeReplacingIndexRef.current = null;
    try {
      const res = await adminAPI.uploadImage(file);
      const url = res.data?.data?.url;
      if (!url) return;
      if (replacing !== null && replacing !== undefined) {
        setLifeForm((p) => {
          const imgs = [...(p.images || [])];
          imgs[replacing] = url;
          return { ...p, images: imgs };
        });
      } else {
        setLifeForm((p) => ({ ...p, images: [...(p.images || []), url] }));
      }
    } catch (err) {
      showToast({ message: err.response?.data?.message || 'Upload failed.', severity: 'error' });
    }
  };

  if (isLoading) return (
    <div className="careers-admin-page module-page">
      <PageHeader title="Join Our Team / Careers" subtitle="Loading careers content…" />
      <Card>
        <CardContent>
          <div className="ops-loading-box">
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="careers-admin-page module-page">
      <PageHeader
        title="Join Our Team / Careers"
        subtitle="Manage the Careers page: hero, why join, life at Invincible, and open positions."
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Hero" />
          <Tab label="Why Join" />
          <Tab label="Life at Invincible" />
          <Tab label="Open Positions" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <Card className="careers-admin-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Hero Banner</Typography>
            <input type="file" accept="image/*" ref={heroImageInputRef} className="careers-hidden-input" onChange={handleHeroImageUpload} />
            {heroForm.imageUrl ? (
              <div className="careers-hero-preview-wrap">
                <div className="careers-hero-preview" style={{ backgroundImage: `url(${heroForm.imageUrl})` }} />
                <Button size="small" startIcon={<Upload size={16} />} onClick={() => heroImageInputRef.current?.click()} sx={{ mt: 1 }}>Change image</Button>
              </div>
            ) : (
              <div className="careers-hero-upload-zone" onClick={() => heroImageInputRef.current?.click()}>
                <ImageIcon size={40} />
                <span>Upload hero image from device</span>
              </div>
            )}
            <div className="careers-form-grid">
              <TextField fullWidth label="Title" value={heroForm.title} onChange={(e) => setHeroForm((p) => ({ ...p, title: e.target.value }))} size="small" placeholder="Careers At Invincible" />
              <TextField fullWidth label="Subtitle" value={heroForm.subtitle} onChange={(e) => setHeroForm((p) => ({ ...p, subtitle: e.target.value }))} size="small" multiline rows={2} sx={{ gridColumn: '1 / -1' }} />
              <TextField fullWidth label="Or paste image URL" value={heroForm.imageUrl} onChange={(e) => setHeroForm((p) => ({ ...p, imageUrl: e.target.value }))} size="small" />
            </div>
            <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => updateHeroMutation.mutate(heroForm)} disabled={updateHeroMutation.isLoading}>Save Hero</Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card className="careers-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Why Join Invincible?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Right-column image (shown in a card on the careers page)</Typography>
            <input type="file" accept="image/*" ref={whyImageInputRef} className="careers-hidden-input" onChange={handleWhyImageUpload} />
            {whyForm.imageUrl ? (
              <div className="careers-hero-preview-wrap">
                <div className="careers-hero-preview careers-why-preview" style={{ backgroundImage: `url(${whyForm.imageUrl})` }} />
                <Button size="small" startIcon={<Upload size={16} />} onClick={() => whyImageInputRef.current?.click()} sx={{ mt: 1 }}>Change image</Button>
              </div>
            ) : (
              <div className="careers-hero-upload-zone careers-why-upload-zone" onClick={() => whyImageInputRef.current?.click()}>
                <ImageIcon size={40} />
                <span>Upload image from device</span>
              </div>
            )}
            <TextField fullWidth label="Or paste image URL" value={whyForm.imageUrl} onChange={(e) => setWhyForm((p) => ({ ...p, imageUrl: e.target.value }))} size="small" sx={{ mt: 1, mb: 2 }} />
            <div className="careers-form-grid">
              <TextField fullWidth label="Section title" value={whyForm.title} onChange={(e) => setWhyForm((p) => ({ ...p, title: e.target.value }))} size="small" />
              <TextField fullWidth multiline rows={4} label="Description" value={whyForm.description} onChange={(e) => setWhyForm((p) => ({ ...p, description: e.target.value }))} size="small" sx={{ gridColumn: '1 / -1' }} />
            </div>
            <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5 }}>Bullet points (with checkmark on frontend)</Typography>
            <div className="careers-bullets-list">
              {(whyForm.bullets || []).map((b, i) => (
                <div key={i} className="careers-bullet-row">
                  <span>{b}</span>
                  <Button size="small" color="error" onClick={() => removeBullet(i)}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
            <div className="careers-add-bullet">
              <TextField size="small" label="New bullet" value={bulletInput} onChange={(e) => setBulletInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBullet())} sx={{ minWidth: 280 }} />
              <Button variant="outlined" size="small" onClick={addBullet}>Add</Button>
            </div>
            <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => updateWhyMutation.mutate(whyForm)} disabled={updateWhyMutation.isLoading}>Save Why Join</Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card className="careers-admin-card">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Life at Invincible</Typography>
            <div className="careers-form-grid">
              <TextField fullWidth label="Section title" value={lifeForm.title} onChange={(e) => setLifeForm((p) => ({ ...p, title: e.target.value }))} size="small" />
              <TextField fullWidth label="Subtitle" value={lifeForm.subtitle} onChange={(e) => setLifeForm((p) => ({ ...p, subtitle: e.target.value }))} size="small" />
            </div>
            <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5 }}>
              Up to <strong>7 photos</strong> for the grid (the middle decorative tile uses no image). Add by URL or upload — save when done.
            </Typography>
            <input type="file" accept="image/*" ref={lifeImageInputRef} className="careers-hidden-input" onChange={handleLifeImageUpload} />
            <div className="careers-life-images-list">
              {(lifeForm.images || []).map((url, i) => (
                <div key={i} className="careers-life-image-row">
                  <div className="careers-life-thumb" style={{ backgroundImage: url ? `url(${url})` : 'none' }} />
                  <div className="careers-life-image-actions">
                    <Button size="small" startIcon={<Upload size={14} />} onClick={() => { lifeReplacingIndexRef.current = i; lifeImageInputRef.current?.click(); }}>Replace</Button>
                    <TextField
                      size="small"
                      label="URL"
                      fullWidth
                      value={url}
                      onChange={(e) =>
                        setLifeForm((p) => {
                          const imgs = [...(p.images || [])];
                          imgs[i] = e.target.value;
                          return { ...p, images: imgs };
                        })
                      }
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                    <Button size="small" color="error" onClick={() => removeLifeImage(i)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
            <AddLifeImageForm onAdd={addLifeImage} onUploadClick={() => { lifeReplacingIndexRef.current = null; lifeImageInputRef.current?.click(); }} />
            <Button variant="contained" sx={{ mt: 1.5 }} onClick={() => updateLifeMutation.mutate(lifeForm)} disabled={updateLifeMutation.isLoading}>Save Life at Invincible</Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Card className="careers-admin-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Section headings</Typography>
            <div className="careers-form-grid">
              <TextField fullWidth label="Title" value={positionsHeadings.title} onChange={(e) => setPositionsHeadings((p) => ({ ...p, title: e.target.value }))} size="small" />
              <TextField fullWidth label="Subtitle" value={positionsHeadings.subtitle} onChange={(e) => setPositionsHeadings((p) => ({ ...p, subtitle: e.target.value }))} size="small" />
            </div>
            <Button variant="contained" sx={{ mt: 1 }} onClick={() => updateHeadingsMutation.mutate(positionsHeadings)} disabled={updateHeadingsMutation.isLoading}>Save headings</Button>
          </CardContent>
        </Card>
        <Card className="careers-admin-card">
          <CardContent>
            <div className="careers-positions-head">
              <Typography variant="subtitle1" fontWeight={600}>Job categories & openings</Typography>
              <AddCategoryButton onCreate={createCategoryMutation.mutate} />
            </div>
            <div className="careers-categories-list">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  onUpdate={(data) => updateCategoryMutation.mutate({ id: cat.id, data })}
                  onDelete={() => window.confirm('Delete this category and all its jobs?') && deleteCategoryMutation.mutate(cat.id)}
                  onCreateJob={(data) => createJobMutation.mutate({ categoryId: cat.id, data })}
                  onUpdateJob={(id, data) => updateJobMutation.mutate({ id, data })}
                  onDeleteJob={(id) => window.confirm('Delete this job?') && deleteJobMutation.mutate(id)}
                />
              ))}
              {categories.length === 0 && <p className="careers-no-categories">No categories yet. Add one to start listing jobs.</p>}
            </div>
          </CardContent>
        </Card>
      </TabPanel>
    </div>
  );
};

function AddLifeImageForm({ onAdd, onUploadClick }) {
  const [url, setUrl] = useState('');
  return (
    <div className="careers-add-life-image">
      {onUploadClick && (
        <Button variant="outlined" size="small" startIcon={<Upload size={16} />} onClick={onUploadClick} sx={{ mr: 1 }}>Upload from device</Button>
      )}
      <TextField size="small" label="Or paste image URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." sx={{ minWidth: 280 }} />
      <Button variant="outlined" size="small" onClick={() => { if (url?.trim()) { onAdd(url.trim()); setUrl(''); } }}>Add image</Button>
    </div>
  );
}

function AddCategoryButton({ onCreate }) {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  const handleCreate = () => {
    if (!name.trim()) return;
    onCreate({ name: name.trim(), icon: 'operations', order: 0, isActive: true });
    setName('');
    setOpen(false);
  };
  if (!open) return <Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setOpen(true)}>Add category</Button>;
  return (
    <div className="careers-add-category-inline">
      <TextField size="small" label="Category name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Operations" sx={{ minWidth: 200 }} />
      <Button variant="contained" size="small" onClick={handleCreate}>Add</Button>
      <Button size="small" onClick={() => { setOpen(false); setName(''); }}>Cancel</Button>
    </div>
  );
}

function CategoryCard({ category, onUpdate, onDelete, onCreateJob, onUpdateJob, onDeleteJob }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(category.name);
  const jobs = category.jobs || [];

  useEffect(() => {
    setName(category.name);
  }, [category.name]);

  const saveName = () => {
    if (name.trim() && name !== category.name) onUpdate({ name: name.trim() });
    setEditingName(false);
  };

  return (
    <div className="careers-category-card">
      <div className="careers-category-header">
        {editingName ? (
          <>
            <TextField size="small" value={name} onChange={(e) => setName(e.target.value)} sx={{ width: 220 }} />
            <Button size="small" onClick={saveName}>Save</Button>
            <Button size="small" onClick={() => { setEditingName(false); setName(category.name); }}>Cancel</Button>
          </>
        ) : (
          <>
            <strong>{category.name}</strong>
            <span className="careers-category-meta">{jobs.length} openings</span>
            <Button size="small" startIcon={<Pencil size={14} />} onClick={() => setEditingName(true)}>Edit</Button>
            <Button size="small" color="error" startIcon={<Trash2 size={14} />} onClick={onDelete}>Delete</Button>
          </>
        )}
      </div>
      <div className="careers-jobs-list">
        {jobs.map((job) => (
          <JobRow key={job.id} job={job} onUpdate={(data) => onUpdateJob(job.id, data)} onDelete={() => onDeleteJob(job.id)} />
        ))}
        <AddJobRow categoryId={category.id} onAdd={onCreateJob} />
      </div>
    </div>
  );
}

function parseList(str) {
  if (!str || typeof str !== 'string') return [];
  try {
    const v = JSON.parse(str);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function JobRow({ job, onUpdate, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    title: job.title,
    location: job.location || '',
    employmentType: job.employmentType || '',
    link: job.link || '',
    description: job.description || '',
    responsibilities: (parseList(job.responsibilities) || []).join('\n'),
    requirements: (parseList(job.requirements) || []).join('\n'),
    salary: job.salary || ''
  });

  const save = () => {
    const payload = {
      ...form,
      responsibilities: form.responsibilities ? JSON.stringify(form.responsibilities.split('\n').map(s => s.trim()).filter(Boolean)) : null,
      requirements: form.requirements ? JSON.stringify(form.requirements.split('\n').map(s => s.trim()).filter(Boolean)) : null
    };
    onUpdate(payload);
    setEdit(false);
  };

  if (edit) {
    return (
      <div className="careers-job-form careers-job-form-expanded">
        <TextField size="small" label="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} fullWidth />
        <TextField size="small" label="Location" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
        <TextField size="small" label="Employment type" value={form.employmentType} onChange={(e) => setForm((p) => ({ ...p, employmentType: e.target.value }))} />
        <TextField size="small" label="Salary" value={form.salary} onChange={(e) => setForm((p) => ({ ...p, salary: e.target.value }))} placeholder="e.g. Competitive" />
        <TextField size="small" label="Link" value={form.link} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))} fullWidth sx={{ gridColumn: '1 / -1' }} />
        <TextField size="small" label="About the Role (description)" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={3} sx={{ gridColumn: '1 / -1' }} />
        <TextField size="small" label="Key Responsibilities (one per line)" value={form.responsibilities} onChange={(e) => setForm((p) => ({ ...p, responsibilities: e.target.value }))} fullWidth multiline rows={4} sx={{ gridColumn: '1 / -1' }} />
        <TextField size="small" label="Requirements (one per line)" value={form.requirements} onChange={(e) => setForm((p) => ({ ...p, requirements: e.target.value }))} fullWidth multiline rows={4} sx={{ gridColumn: '1 / -1' }} />
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1 }}>
          <Button size="small" variant="contained" onClick={save}>Save</Button>
          <Button size="small" onClick={() => setEdit(false)}>Cancel</Button>
        </Box>
      </div>
    );
  }
  return (
    <div className="careers-job-row">
      <span>{job.title}</span>
      <span className="careers-job-meta">{[job.location, job.employmentType].filter(Boolean).join(' • ') || '—'}</span>
      <Button size="small" onClick={() => setEdit(true)}><Pencil size={14} /></Button>
      <Button size="small" color="error" onClick={onDelete}><Trash2 size={14} /></Button>
    </div>
  );
}

function AddJobRow({ onAdd }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: '', location: '', employmentType: 'Full-Time', link: '', description: '', responsibilities: '', requirements: '', salary: '' });

  const handleAdd = () => {
    if (!form.title.trim()) return;
    const payload = {
      ...form,
      responsibilities: form.responsibilities ? JSON.stringify(form.responsibilities.split('\n').map(s => s.trim()).filter(Boolean)) : null,
      requirements: form.requirements ? JSON.stringify(form.requirements.split('\n').map(s => s.trim()).filter(Boolean)) : null
    };
    onAdd(payload);
    setForm({ title: '', location: '', employmentType: 'Full-Time', link: '', description: '', responsibilities: '', requirements: '', salary: '' });
    setShow(false);
  };

  if (!show) return <Button size="small" startIcon={<Plus size={14} />} onClick={() => setShow(true)} sx={{ mt: 0.5 }}>Add job</Button>;
  return (
    <div className="careers-job-form careers-job-form-expanded">
      <TextField size="small" label="Title *" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} fullWidth />
      <TextField size="small" label="Location" value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
      <TextField size="small" label="Employment type" value={form.employmentType} onChange={(e) => setForm((p) => ({ ...p, employmentType: e.target.value }))} />
      <TextField size="small" label="Salary" value={form.salary} onChange={(e) => setForm((p) => ({ ...p, salary: e.target.value }))} placeholder="e.g. Competitive" />
      <TextField size="small" label="Link" value={form.link} onChange={(e) => setForm((p) => ({ ...p, link: e.target.value }))} fullWidth sx={{ gridColumn: '1 / -1' }} />
      <TextField size="small" label="About the Role" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} fullWidth multiline rows={2} sx={{ gridColumn: '1 / -1' }} />
      <TextField size="small" label="Responsibilities (one per line)" value={form.responsibilities} onChange={(e) => setForm((p) => ({ ...p, responsibilities: e.target.value }))} fullWidth multiline rows={3} sx={{ gridColumn: '1 / -1' }} />
      <TextField size="small" label="Requirements (one per line)" value={form.requirements} onChange={(e) => setForm((p) => ({ ...p, requirements: e.target.value }))} fullWidth multiline rows={3} sx={{ gridColumn: '1 / -1' }} />
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={handleAdd}>Add job</Button>
        <Button size="small" onClick={() => setShow(false)}>Cancel</Button>
      </Box>
    </div>
  );
}

export default CareersManagement;
